<script>
  import Session from './Session.svelte'
  import { decodeWord } from './wordEngine.js'
  import { buildCustomWord } from './content.js'
  import { loadAudioManifest } from './audio.js'
  import { itemById, earlierWords, UNITS, STAGES } from './curriculum/b1.js'
  import {
    loadProgress, saveProgress, currentUnit, dueItemIds, recordItem, finishSession, logSession,
    displayName, kpis, chapters, isMastered, unitStars, shakyItems, addPracticeMore,
  } from './curriculum/progress.js'

  // Entry modes: 'review' (Recommended repetition) · 'grid' (unit grid + KPIs) ·
  // 'current' (jump into the current unit). All practice runs in the shared Session.
  let { child, intent = 'current', onexit } = $props()

  let progress = $state(loadProgress(child))
  let phase = $state(intent === 'grid' ? 'pick' : 'loading')
  let sessionSteps = $state([])
  let sessionTitle = $state('')
  let sessionKind = $state('unit') // 'unit' | 'review'
  let sessionUnit = null
  let sessionKey = $state(0) // remounts Session per new session

  const k = $derived(kpis(progress))
  const chaps = $derived(chapters(progress))
  const shaky = $derived(shakyItems(progress))
  const backLabel = intent === 'grid' ? '‹ Lessons' : '‹ Menu'

  let bootstrapped = false
  $effect(() => {
    if (bootstrapped) return
    bootstrapped = true
    if (intent === 'review') startReview()
    else if (intent === 'current') startSession(currentUnit(progress))
  })

  function enrich(text) { return buildCustomWord(text, decodeWord(text)) }

  async function startSession(u) {
    phase = 'loading'
    sessionUnit = u || currentUnit(progress)
    sessionKind = 'unit'
    const built = []
    const manifest = {}
    const addWord = (text, type, review) => { const e = enrich(text); Object.assign(manifest, e.manifest); built.push({ type, text, word: e.word, review }) }
    for (const id of sessionUnit.new_items) { const item = itemById(id); if (item) built.push({ type: 'intro', item }) }
    const due = new Set(dueItemIds(progress))
    if (due.size) {
      const seen = new Set(sessionUnit.words)
      let n = 0
      for (const w of earlierWords(sessionUnit.order)) {
        if (seen.has(w)) continue
        if (decodeWord(w).some((s) => due.has(s.text))) { seen.add(w); addWord(w, 'word', true); if (++n >= 3) break }
      }
    }
    for (const w of sessionUnit.words) addWord(w, 'word', false)
    for (const w of sessionUnit.tricky) addWord(w, 'tricky', false)
    await loadAudioManifest(manifest)
    sessionSteps = built
    sessionTitle = `Unit ${sessionUnit.order} · ${sessionUnit.title}`
    sessionKey += 1
    phase = 'session'
  }

  async function startReview() {
    phase = 'loading'
    sessionKind = 'review'
    const due = new Set(dueItemIds(progress))
    const pool = earlierWords(progress.current_order + 1)
    const picked = []; const seen = new Set()
    const add = (w) => { if (!seen.has(w)) { seen.add(w); picked.push(w) } }
    for (const w of progress.practiceMore) { if (picked.length >= 12) break; add(w) } // flagged words first
    if (due.size) for (const w of pool) { if (picked.length >= 12) break; if (decodeWord(w).some((s) => due.has(s.text))) add(w) }
    for (let i = pool.length - 1; i >= 0 && picked.length < 8; i--) add(pool[i])
    const built = []; const manifest = {}
    for (const w of picked) { const e = enrich(w); Object.assign(manifest, e.manifest); built.push({ type: 'word', text: w, word: e.word, review: true }) }
    await loadAudioManifest(manifest)
    sessionSteps = built
    sessionTitle = 'Practice more'
    sessionKey += 1
    phase = built.length ? 'session' : 'pick'
  }

  function applySpacing(results) {
    for (const r of results) for (const seg of decodeWord(r.step.text)) if (itemById(seg.text)) recordItem(progress, seg.text, r.correct)
  }
  function unitFinish({ results, minutes }) {
    applySpacing(results)
    const unitR = results.filter((r) => !r.step.review)
    const uacc = unitR.length ? unitR.filter((r) => r.correct).length / unitR.length : 1
    const { mastered, advanced } = finishSession(progress, sessionUnit, uacc, minutes)
    saveProgress(progress)
    return advanced ? `🎉 Unit mastered! Next: Unit ${progress.current_order}.` : mastered ? '🎉 Great day — one more good day to move on.' : 'Keep practicing this unit.'
  }
  function reviewFinish({ results, accuracy, minutes }) {
    applySpacing(results)
    logSession(progress, 'review', accuracy, minutes)
    saveProgress(progress)
    return `🔁 ${results.length} words reviewed`
  }
  function onSessionFinish(payload) { return sessionKind === 'review' ? reviewFinish(payload) : unitFinish(payload) }
  // "Practice more" from a unit session → add its words to the child's pool.
  function onPracticeMore() { if (sessionUnit) { addPracticeMore(progress, sessionUnit.words); saveProgress(progress) } }

  function backAfterSession() {
    progress = loadProgress(child)
    if (intent === 'grid') phase = 'pick'
    else onexit()
  }
</script>

{#if phase === 'session'}
  {#key sessionKey}
    <Session title={sessionTitle} steps={sessionSteps} {backLabel} onexit={backAfterSession} onfinish={onSessionFinish} onpracticemore={sessionKind === 'review' ? undefined : onPracticeMore} source={sessionKind === 'review' ? 'review' : 'lesson'} {child} />
  {/key}
{:else}
  <div class="lessons" class:scroll={phase === 'pick' || phase === 'report'}>
    <div class="toolbar"><button class="tb" onclick={onexit}>‹ Menu</button></div>

    {#if phase === 'pick'}
      <div class="pickwrap">
        <div class="kpis">
          <div class="kpi"><b>{k.percent}%</b><span>complete</span></div>
          <div class="kpi"><b>{k.mastered}/{k.total}</b><span>units</span></div>
          <div class="kpi"><b>🔥{k.streak}</b><span>streak</span></div>
          <div class="kpi"><b>{k.learned}</b><span>sounds</span></div>
          <button class="kbtn" onclick={startReview}>🔁 Practice more{progress.practiceMore.length || k.due ? ` (${progress.practiceMore.length + k.due})` : ''}</button>
          <button class="kbtn report" onclick={() => (phase = 'report')}>📊 Report</button>
        </div>
        {#each chaps as ch}
          <div class="chapter"><div class="chead"><span>Stage {ch.stage} · {STAGES[ch.stage] || ''}</span><span>{ch.mastered}/{ch.total}</span></div><div class="cbar"><span style={`width:${ch.percent}%`}></span></div></div>
        {/each}
        <div class="grid">
          {#each UNITS as u}
            {@const mastered = isMastered(progress, u)}
            {@const current = u.order === progress.current_order}
            {@const stars = unitStars(progress, u)}
            <button class="unit" class:mastered class:current class:upcoming={u.order > progress.current_order && !mastered} onclick={() => startSession(u)}>
              <span class="uno">{mastered ? '✓' : u.order}</span>
              <span class="utitle">{u.title}</span>
              <span class="uitems">{u.new_items.map((id) => (itemById(id) || {}).display).join(' ')}</span>
              <span class="ustars">{'★'.repeat(stars)}{'☆'.repeat(3 - stars)}</span>
            </button>
          {/each}
        </div>
      </div>
    {:else if phase === 'report'}
      <div class="pickwrap">
        <h2 class="rtitle">{displayName(child)} · Progress</h2>
        <div class="bigk">
          <div class="bk"><b>{k.percent}%</b><span>complete</span></div>
          <div class="bk"><b>{k.mastered}/{k.total}</b><span>units mastered</span></div>
          <div class="bk"><b>{k.streak}</b><span>day streak</span></div>
          <div class="bk"><b>{k.learned}</b><span>sounds learned</span></div>
          <div class="bk"><b>{k.sessions}</b><span>sessions</span></div>
          <div class="bk"><b>{k.due}</b><span>due today</span></div>
        </div>
        {#each chaps as ch}
          <div class="chapter"><div class="chead"><span>Stage {ch.stage} · {STAGES[ch.stage] || ''}</span><span>{ch.mastered}/{ch.total} · {ch.percent}%</span></div><div class="cbar"><span style={`width:${ch.percent}%`}></span></div></div>
        {/each}
        {#if shaky.length}<p class="shaky"><b>Needs practice:</b> {shaky.map((id) => (itemById(id) || { display: id }).display).join('  ')}</p>{/if}
        <button class="go" onclick={() => (phase = 'pick')}>‹ Back to lessons</button>
      </div>
    {:else}
      <p class="dim">Loading sounds…</p>
    {/if}
  </div>
{/if}

<style>
  .lessons { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2vh; padding: 2vh; box-sizing: border-box; }
  .lessons.scroll { justify-content: flex-start; overflow-y: auto; padding-top: 7vh; position: fixed; inset: 0; }
  .toolbar { position: fixed; top: 1.2vh; left: 1.4vh; z-index: 25; display: flex; gap: 1vh; }
  .tb { border: none; background: rgba(255,255,255,0.9); color: #2e5d1f; font-family: inherit; font-weight: 800; font-size: 1.7vh; padding: 0.8vh 1.5vh; border-radius: 999px; box-shadow: 0 3px 0 rgba(0,0,0,0.15); cursor: pointer; }
  .tb:active { transform: translateY(2px); box-shadow: 0 1px 0 rgba(0,0,0,0.15); }

  .pickwrap { width: min(1000px, 96vw); display: flex; flex-direction: column; gap: 1.6vh; }
  .kpis { display: flex; flex-wrap: wrap; gap: 1vh; align-items: stretch; justify-content: center; }
  .kpi { background: rgba(255,255,255,0.92); border-radius: 14px; padding: 1vh 1.6vh; text-align: center; min-width: 12vh; display: flex; flex-direction: column; }
  .kpi b { color: #2e5d1f; font-size: 2.6vh; }
  .kpi span { color: #4a7a34; font-size: 1.4vh; font-weight: 700; }
  .kbtn { border: none; font-family: inherit; font-weight: 800; font-size: 1.9vh; color: #7a4a00; background: #ffd24d; border-radius: 14px; padding: 0 2vh; cursor: pointer; box-shadow: 0 4px 0 rgba(0,0,0,0.15); }
  .kbtn.report { background: #fffef0; color: #2e5d1f; }
  .kbtn:active { transform: translateY(2px); box-shadow: 0 2px 0 rgba(0,0,0,0.15); }
  .chapter { background: rgba(255,255,255,0.2); border-radius: 12px; padding: 1vh 1.4vh; }
  .chead { display: flex; justify-content: space-between; color: #fffef0; font-weight: 800; font-size: 1.7vh; margin-bottom: 0.6vh; }
  .cbar { height: 1.1vh; background: rgba(0,0,0,0.15); border-radius: 999px; overflow: hidden; }
  .cbar span { display: block; height: 100%; background: #ffd24d; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(15vh, 1fr)); gap: 1.2vh; }
  .unit { border: none; background: #fffef0; border-radius: 16px; padding: 1.4vh 1vh; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 0.4vh; box-shadow: 0 5px 0 rgba(0,0,0,0.14); text-align: center; }
  .unit:active { transform: translateY(2px); box-shadow: 0 3px 0 rgba(0,0,0,0.14); }
  .unit.current { outline: 0.5vh solid #ffd24d; }
  .unit.mastered { background: #dff2d6; }
  .unit.upcoming { opacity: 0.62; }
  .uno { width: 3.4vh; height: 3.4vh; border-radius: 50%; background: #2e5d1f; color: #fffef0; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 1.8vh; }
  .unit.mastered .uno { background: #3aa655; }
  .utitle { color: #2e5d1f; font-weight: 800; font-size: 1.5vh; line-height: 1.1; }
  .uitems { color: #4a7a34; font-weight: 800; font-size: 1.8vh; letter-spacing: 0.1vh; }
  .ustars { color: #e8a400; font-size: 1.6vh; }
  .rtitle { color: #fffef0; text-align: center; margin: 0; font-size: 3vh; text-shadow: 0 2px 0 rgba(0,0,0,0.15); }
  .bigk { display: grid; grid-template-columns: repeat(auto-fit, minmax(16vh, 1fr)); gap: 1.2vh; }
  .bk { background: #fffef0; border-radius: 14px; padding: 1.6vh; text-align: center; display: flex; flex-direction: column; }
  .bk b { color: #2e5d1f; font-size: 3.4vh; }
  .bk span { color: #4a7a34; font-weight: 700; font-size: 1.5vh; }
  .shaky { background: #fff2cf; border-radius: 12px; padding: 1.2vh 1.6vh; color: #7a4a00; font-weight: 700; margin: 0; }
  .go { border: none; background: #ff7043; color: #fff; font-family: inherit; font-weight: 800; font-size: 2.2vh; padding: 1.2vh 2.8vh; border-radius: 999px; box-shadow: 0 5px 0 rgba(0,0,0,0.18); cursor: pointer; align-self: center; }
  .dim { color: #fffef0; font-weight: 700; }
</style>
